import React, { useState, useEffect } from 'react';
import './BookmarkManager.css';
import { useToast } from './Toast';
import ConfirmDialog from './ConfirmDialog';

const BACKEND_URL = 'https://al-bcd27e7e0130466f87e7390ee3784d32.ecs.us-east-1.on.aws';

const BookmarkManager = ({
  report,
  username,
  reportId,
  isVisible,
  onClose
}) => {
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkName, setBookmarkName] = useState('');
  const [bookmarkDescription, setBookmarkDescription] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // Estado para el diálogo de confirmación
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    bookmarkId: null,
    bookmarkName: ''
  });

  // Cargar bookmarks del usuario
  useEffect(() => {
    if (isVisible && username && reportId) {
      loadBookmarks();
    }
  }, [isVisible, username, reportId]);

  const loadBookmarks = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/bookmarks?username=${username}&reportId=${reportId}`
      );

      if (!response.ok) {
        throw new Error('Error cargando bookmarks');
      }

      const data = await response.json();
      setBookmarks(data.bookmarks || []);
      console.log(`✅ ${data.bookmarks?.length || 0} bookmarks cargados`);
    } catch (error) {
      console.error('❌ Error cargando bookmarks:', error);
      setBookmarks([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Capturar estado actual
  const handleCaptureBookmark = async () => {
    if (!report) {
      toast.showWarning('Reporte no disponible');
      return;
    }

    if (!bookmarkName.trim()) {
      toast.showWarning('Ingresa un nombre para el filtro');
      return;
    }

    setIsCapturing(true);
    try {
      console.log('📸 Capturando estado actual del reporte...');

      // Verificar que bookmarksManager existe
      if (!report.bookmarksManager) {
        throw new Error('BookmarksManager no disponible en este reporte');
      }

      // Capturar usando la API de Power BI
      const capturedBookmark = await report.bookmarksManager.capture({
        personalizeVisuals: true,  // Incluir cambios de personalización
        allPages: false            // Solo página actual
      });

      const bookmarkState = capturedBookmark.state;  // Base64 string

      console.log('✅ Estado capturado, guardando en backend...');

      // Guardar en backend
      const response = await fetch(
        `${BACKEND_URL}/api/bookmarks`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            reportId,
            bookmarkName: bookmarkName.trim(),
            bookmarkState,
            description: bookmarkDescription.trim()
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error guardando bookmark');
      }

      console.log('✅ Bookmark guardado en backend');
      toast.showSuccess(`Filtros "${bookmarkName.trim()}" guardados exitosamente`);

      // Limpiar formulario
      setBookmarkName('');
      setBookmarkDescription('');

      // Recargar lista
      await loadBookmarks();

    } catch (error) {
      console.error('❌ Error capturando bookmark:', error);
      toast.showError(`Error al guardar: ${error.message}`);
    } finally {
      setIsCapturing(false);
    }
  };

  // Aplicar bookmark guardado
  const handleApplyBookmark = async (bookmark) => {
    if (!report) {
      toast.showWarning('Reporte no disponible');
      return;
    }

    try {
      console.log(`🔄 Aplicando bookmark: ${bookmark.bookmark_name}`);

      // Verificar que bookmarksManager existe
      if (!report.bookmarksManager) {
        throw new Error('BookmarksManager no disponible en este reporte');
      }

      // Aplicar usando la API de Power BI
      await report.bookmarksManager.applyState(bookmark.bookmark_state);

      console.log('✅ Bookmark aplicado exitosamente');
      toast.showSuccess(`Filtros "${bookmark.bookmark_name}" aplicados`);

      // Cerrar el modal después de aplicar exitosamente
      onClose();

    } catch (error) {
      console.error('❌ Error aplicando bookmark:', error);
      toast.showError(`Error aplicando filtros: ${error.message}`);
    }
  };

  // Abrir diálogo de confirmación
  const handleDeleteClick = (bookmarkId, bookmarkName) => {
    setConfirmDialog({
      isOpen: true,
      bookmarkId,
      bookmarkName
    });
  };

  // Cancelar eliminación
  const handleCancelDelete = () => {
    setConfirmDialog({
      isOpen: false,
      bookmarkId: null,
      bookmarkName: ''
    });
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    const { bookmarkId, bookmarkName } = confirmDialog;

    // Cerrar diálogo
    setConfirmDialog({
      isOpen: false,
      bookmarkId: null,
      bookmarkName: ''
    });

    try {
      console.log(`🗑️ Eliminando bookmark ID: ${bookmarkId}`);

      const response = await fetch(
        `${BACKEND_URL}/api/bookmarks?id=${bookmarkId}&username=${encodeURIComponent(username)}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Error del servidor:', data);
        throw new Error(data.error || 'Error eliminando filtro');
      }

      console.log('✅ Bookmark eliminado exitosamente');
      toast.showSuccess(`Filtro "${bookmarkName}" eliminado`);
      await loadBookmarks();

    } catch (error) {
      console.error('❌ Error eliminando bookmark:', error);
      toast.showError(`Error al eliminar: ${error.message}`);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="bookmark-manager-overlay" onClick={onClose}>
      <div className="bookmark-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bookmark-manager-header">
          <h2>📑 Mis Filtros Guardados</h2>
          <button className="close-btn" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className="bookmark-manager-body">
          {/* Sección: Guardar Nuevo Bookmark */}
          <div className="capture-section">
            <h3>💾 Guardar Filtros Actuales</h3>
            <input
              type="text"
              placeholder="Nombre del filtro (ej: 'Q1 2024 - Región Norte')"
              value={bookmarkName}
              onChange={(e) => setBookmarkName(e.target.value)}
              maxLength={100}
              className="bookmark-input"
            />
            <textarea
              placeholder="Descripción (opcional)"
              value={bookmarkDescription}
              onChange={(e) => setBookmarkDescription(e.target.value)}
              maxLength={250}
              rows={2}
              className="bookmark-textarea"
            />
            <button
              className="btn-capture"
              onClick={handleCaptureBookmark}
              disabled={isCapturing || !bookmarkName.trim()}
            >
              {isCapturing ? '📸 Guardando...' : '💾 Guardar Filtros Actuales'}
            </button>
          </div>

          <div className="divider"></div>

          {/* Sección: Lista de Bookmarks */}
          <div className="bookmarks-list-section">
            <h3>📂 Filtros Guardados ({bookmarks.length})</h3>

            {isLoading ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Cargando filtros guardados...</p>
              </div>
            ) : bookmarks.length === 0 ? (
              <div className="empty-state">
                <p>📭 No tienes filtros guardados aún.</p>
                <p>Ajusta los filtros del reporte y guárdalos arriba.</p>
              </div>
            ) : (
              <div className="bookmarks-list">
                {bookmarks.map((bookmark) => (
                  <div key={bookmark.id} className="bookmark-item">
                    <div className="bookmark-info">
                      <div className="bookmark-name">
                        🔖 {bookmark.bookmark_name}
                        {bookmark.is_default && <span className="default-badge">Por defecto</span>}
                      </div>
                      {bookmark.description && (
                        <div className="bookmark-description">{bookmark.description}</div>
                      )}
                      <div className="bookmark-meta">
                        Guardado: {new Date(bookmark.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    <div className="bookmark-actions">
                      <button
                        className="btn-apply"
                        onClick={() => handleApplyBookmark(bookmark)}
                        title="Aplicar estos filtros"
                      >
                        ✓ Aplicar
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteClick(bookmark.id, bookmark.bookmark_name)}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Diálogo de Confirmación */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        type="danger"
        title="Eliminar Filtro"
        message={`¿Estás seguro que deseas eliminar "${confirmDialog.bookmarkName}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default BookmarkManager;
